import React, { useState, useEffect } from 'react';
import { testimonials } from '../data/mockData';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const section = useScrollAnimation({ threshold: 0.15 });

  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
      setTimeout(() => setIsAnimating(false), 300);
    }, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex]);

  const handleNext = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    setTimeout(() => setIsAnimating(false), 300);
  };

  const handlePrev = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    setTimeout(() => setIsAnimating(false), 300);
  };

  const t = testimonials[currentIndex];

  return (
    <section id="testimonials" data-testid="testimonials-section" ref={section.ref} className="py-24 md:py-32 bg-[#111827] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
        <div className={`text-center max-w-2xl mx-auto mb-14 scroll-fade-up ${section.isVisible ? 'scroll-visible' : ''}`}>
          <span className="text-xs font-bold uppercase tracking-widest text-[#FF1010] block mb-3">Real Reviews</span>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tighter text-white mb-4 uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Don't Take Our Word For It
          </h2>
          <p className="text-base text-white/60 leading-relaxed">
            Thousands of businesses already protected their brands with us. Here is what they say.
          </p>
        </div>

        <div className={`max-w-3xl mx-auto scroll-scale-up scroll-delay-2 ${section.isVisible ? 'scroll-visible' : ''}`}>
          <div
            data-testid="testimonial-card"
            className="bg-white border-2 border-[#111827] p-10 md:p-14 text-center shadow-[8px_8px_0px_#FF1010] transition-opacity duration-300"
            style={{ opacity: isAnimating ? 0.4 : 1 }}
          >
            <div className="flex items-center justify-center gap-0.5 mb-6">
              {[...Array(t.rating)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-[#FF1010] text-[#FF1010]" />
              ))}
            </div>

            <blockquote className="text-lg md:text-xl font-medium text-[#111827] leading-relaxed mb-8" style={{ fontFamily: "'Outfit', sans-serif" }}>
              &ldquo;{t.content}&rdquo;
            </blockquote>

            <div className="flex items-center justify-center gap-3">
              <div className="w-10 h-10 bg-[#FF1010] flex items-center justify-center text-white font-black text-lg" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {t.name.charAt(0)}
              </div>
              <div className="text-left">
                <div className="font-bold text-[#111827] text-sm">{t.name}</div>
                <div className="text-xs text-[#4B5563]">{t.role}</div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-center gap-5 mt-8">
            <Button data-testid="testimonial-prev" onClick={handlePrev} disabled={isAnimating} variant="outline" className="w-10 h-10 p-0 border-white/20 bg-transparent hover:bg-white/10 text-white rounded-none">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              {testimonials.map((_, index) => (
                <button key={index} data-testid={`testimonial-dot-${index}`} onClick={() => { if (!isAnimating) { setIsAnimating(true); setCurrentIndex(index); setTimeout(() => setIsAnimating(false), 300); } }} className={`transition-all duration-200 ${index === currentIndex ? 'w-8 h-2 bg-[#FF1010]' : 'w-2 h-2 bg-white/30 hover:bg-white/50'}`} />
              ))}
            </div>
            <Button data-testid="testimonial-next" onClick={handleNext} disabled={isAnimating} variant="outline" className="w-10 h-10 p-0 border-white/20 bg-transparent hover:bg-white/10 text-white rounded-none">
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
